import Generic from 'src/models/generic/Generic'
import GiftCard from 'src/models/gift-card/GiftCard'
import Model, { encoders, executeMutation, GqlError, ID, IsoDateTime, JsonArray, JsonObject } from 'src/models/model'
import Order from 'src/models/order/Order'
import PaymentType from 'src/models/payment/PaymentType'
import RegisterStation from 'src/models/register/RegisterStation'
import User from 'src/models/user/User'

export const PAYMENT_TYPES = {
  CASH: 'cash',
  CREDIT: 'credit',
  GIFT_CARD: 'gift-card',
  CARD: 'card',
  REMOTE: 'remote',
  CUSTOM: 'custom'
}
PAYMENT_TYPES.ALL = _.values(PAYMENT_TYPES)

export const PAYMENT_SUBTYPES = {
  MANUAL: 'manual',
  PRESENT: 'present',
  SAVED: 'saved',
  SAVED_TAB: 'saved_tab',
  WALLET: 'wallet',
  WINDCAVE: 'windcave'
}
PAYMENT_SUBTYPES.ALL = _.values(PAYMENT_SUBTYPES)

export const STRIPE_PAYMENT_SUBTYPES = {
  MANUAL: 'manual',
  PRESENT: 'present',
  SAVED: 'saved',
  WALLET: 'wallet',
  SAVED_TAB: 'saved_tab'
}

export const PAYMENT_STATES = {
  DRAFT: 'draft',
  COMPLETED: 'completed',
  CANCELED: 'canceled',
  DISPUTED: 'disputed',
  LOST_DISPUTE: 'lost_dispute',
  PENDING: 'pending',
  PRE_AUTH: 'pre_auth'
}
PAYMENT_STATES.ALL = _.values(PAYMENT_STATES)
PAYMENT_STATES.OPERATIVE = [
  PAYMENT_STATES.PRE_AUTH,
  PAYMENT_STATES.DRAFT,
  PAYMENT_STATES.COMPLETED,
  PAYMENT_STATES.DISPUTED,
  PAYMENT_STATES.LOST_DISPUTE
]
// those are brand support by both stripe and font awesome icon set
export const PAYMENT_CARD_PROVIDER = {
  VISA: 'visa',
  MASTERCARD: 'mastercard',
  AMEX: 'amex',
  DISCOVER: 'discover',
  JCB: 'jcb',
  DINERS: 'diners-club'
}

export const STRIPE_PAYMENT_METHODS = Object.freeze({
  CARD: 'card',
  LINK: 'link'
})

export const STRIPE_WALLET_PAYMENT_METHODS = Object.freeze({
  APPLE_PAY: 'apple_pay',
  GOOGLE_PAY: 'google_pay',
  link: 'link'
})

export default class Payment extends Model {
  static name = 'Payment'

  static isExportable = true

  static versioned = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Customer]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      type: {
        type: String,
        default: null,
        options: PAYMENT_TYPES.ALL,
        sortable: true
      },
      subtype: {
        type: String,
        options: PAYMENT_SUBTYPES.ALL,
        default: null
      },
      state: {
        type: String,
        output: true,
        default: 'draft',
        options: PAYMENT_STATES.ALL,
        sortable: true
      },
      paidAt: {
        type: IsoDateTime,
        default: null,
        output: true,
        sortable: true
      },
      disputedAt: {
        type: IsoDateTime,
        default: null,
        output: true,
        sortable: true
      },
      disputeDecidedAt: {
        type: IsoDateTime,
        default: null,
        output: true
      },
      disputedState: {
        type: String,
        default: null,
        output: true
      },
      disputeId: {
        type: String,
        default: null,
        output: true
      },
      stripePaymentMethodId: {
        type: String,
        default: null,
        sortable: true
      },
      stripePaymentMethodFingerprint: {
        type: String,
        default: null,
        sortable: true
      },
      stripePaymentIntentId: {
        type: String,
        default: null,
        sortable: true
      },
      stripeFailureMessage: {
        type: String,
        default: null,
        output: true,
        sortable: true
      },
      stripeChargeId: {
        type: String,
        output: true
      },
      isInteracPayment: {
        type: Boolean,
        output: true
      },
      giftCard: {
        type: GiftCard,
        encode: encoders.hasOne
      },
      amount: {
        type: Number,
        encode: encoders.cents,
        default: 0,
        sortable: true
      },
      cashReceived: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      changeDue: {
        type: Number,
        local: true,
        default: 0,
        computed () {
          if (this.type !== PAYMENT_TYPES.CASH) return 0

          if (this.forceShowChangeDue) {
            return this.cashReceived - this.amount
          } else {
            return this.finalCashReceived - this.amount
          }
        }
      },
      cardType: {
        type: String
      },
      lastFour: {
        type: String
      },
      nameOnCard: {
        type: String
      },
      registerStation: {
        type: RegisterStation,
        reference: true,
        encode: encoders.hasOne
      },
      paymentType: {
        type: PaymentType,
        reference: true,
        encode: encoders.hasOne
      },
      paymentTypeReasonText: {
        type: String,
        default: null
      },
      connectorPaymentReferences: {
        type: JsonArray,
        output: true,
        default () {
          return []
        },
        excludeFromDefault: true
      },
      employee: {
        type: User,
        reference: true,
        output: true
      },
      disputeFee: {
        type: Number,
        local: true,
        default: 15
      },
      stripeFee: {
        type: Number,
        output: true
      },
      indyFee: {
        type: Number,
        output: true
      },
      paymentConnectorStatusDetails: {
        type: JsonObject,
        output: true
      },
      paymentConnectorActionToExecute: {
        type: JsonObject,
        input: true
      },
      refundFromPaymentId: {
        type: ID,
        input: true,
        default: null
      },
      paymentConnectorCanVoid: {
        type: Boolean,
        output: true,
        default: false
      },
      paymentConnectorVoid: {
        type: Boolean,
        input: true,
        default: false
      },
      order: {
        type: Order,
        reference: true,
        output: true
      },
      isSavingPreAuth: {
        type: Boolean,
        input: true,
        default: false
      },
      createdAt: {
        type: IsoDateTime,
        output: true,
        sortable: true
      },
      orderItemIdsToVoid: {
        type: JsonArray,
        default: () => { return [] },
        output: true
      },
      forceShowChangeDue: {
        type: Boolean,
        default: false,
        local: true
      }
    }

    this.computed = {
      draft () {
        return this.state === PAYMENT_STATES.DRAFT
      },
      operative () {
        return _.includes(PAYMENT_STATES.OPERATIVE, this.state)
      },
      completed () {
        return this.state === PAYMENT_STATES.COMPLETED || this.state === PAYMENT_STATES.DISPUTED || this.state === PAYMENT_STATES.LOST_DISPUTE || this.state === PAYMENT_STATES.PENDING
      },
      nonStripeCardPayment () {
        return this.type === PAYMENT_TYPES.CARD && !this.isStripe
      },
      isStripe () {
        return this.type === PAYMENT_TYPES.CARD && (_.includes(STRIPE_PAYMENT_SUBTYPES, this.subtype) || !this.subtype)
      }
    }

    this.queries = {
      stripeDestinationPayment: {
        arguments: {
          paymentId: {
            type: ID,
            required: true
          }
        },
        results: {
          value: Generic
        }
      }
    }

    this.mutations = {
      refundPayment: {
        arguments: {
          stripePaymentIntentId: {
            type: String,
            required: true
          }
        },
        results: {
          payment: Payment,
          errors: [GqlError]
        }
      },
      cancelPreAuth: {
        arguments: {
          id: {
            type: ID,
            required: true
          }
        },
        results: {
          payment: Payment,
          errors: [GqlError]
        }
      }
    }

    this.paginate = true
  }

  get details () {
    if (this.type === PAYMENT_TYPES.CARD) {
      return (_.startCase(this.cardType || '') + ' ...' + (this.lastFour || ''))
    } else if (this.type === PAYMENT_TYPES.CASH) {
      return `${_.startCase(this.type)}${this.changeDue ? ` - ${this.i18n.n(this.changeDue, 'currency')} Change` : ''}`
    } else if (this.paymentType?.name) {
      return `${this.paymentType.name}${this.paymentTypeReasonText ? ` - ${this.paymentTypeReasonText}` : ''}`
    } else {
      return _.startCase(this.type)
    }
  }

  get paymentConnectorResponseMessage () {
    if (!this.paymentConnectorStatusDetails?.response_string) return 'Unknown'
    return this.paymentConnectorStatusDetails?.response_string
  }

  get paymentConnectorResponseCode () {
    if (!this.paymentConnectorStatusDetails?.response_code) return 'Unknown'
    return this.paymentConnectorStatusDetails?.response_code
  }

  get paymentConnectorActionButtons () {
    if (!this.paymentConnectorStatusDetails?.action_buttons) return []
    return this.paymentConnectorStatusDetails?.action_buttons
  }

  get paymentConnectorActionRequired () {
    if (this.paymentConnectorStatusDetails?.requires_action) {
      return true
    } else {
      return false
    }
  }

  get finalCashReceived () {
    return this.state === PAYMENT_STATES.DRAFT ? this.cashReceived : this.amount
  }

  get label () {
    return _([this.state, this.type, this.subtype]).compact().map(_.startCase).value().join(' ')
  }

  get description () {
    return this.configuration.currentCircuit.localeCurrencySymbol + this.amount + ' ' + this.details + ' Order: ' + this.order.id
  }

  get paymentDate () {
    if (!this.paidAt) return null

    return this.configuration.currentSite.siteEffectiveDateForTime(this.paidAt)
  }

  async refund () {
    const results = await executeMutation(
      'refundPayment',
      {
        stripePaymentIntentId: this.stripePaymentIntentId
      },
      {
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async cancelPreAuth () {
    const results = await executeMutation(
      'cancelPreAuth',
      {
        id: this.id
      },
      {
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }
}
